/* global — GRITT by Leatt theme.js
 * Main JS entry point. type="module" defer.
 * Handles: vertical toggle, sticky header, mobile menu, announcement bar,
 * SHOP dropdown, event delegation, reduced motion.
 * Search lives in its own asset (search.js), not here.
 * ES2020+, no frameworks, no bundler.
 */

// ─── Reduced motion ──────────────────────────────────────────────────────────

export const prefersReducedMotion = window.matchMedia(
  '(prefers-reduced-motion: reduce)'
).matches;

// ─── Cookie helpers ───────────────────────────────────────────────────────────

/**
 * Read a cookie value by name.
 * @param {string} name
 * @returns {string|null}
 */
export function getCookie(name) {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

/**
 * Set a cookie.
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
export function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

// ─── Internal state ───────────────────────────────────────────────────────────

/** @type {HTMLButtonElement|null} */
let mobileMenuTrigger = null;

// ─── Vertical toggle ──────────────────────────────────────────────────────────

/**
 * Derive the active vertical from the current page path or cookie.
 * @returns {'motorcycle'|'bicycle'|null}
 */
function resolveActiveVertical() {
  const path = window.location.pathname;
  if (path.includes('/pages/motorcycle')) return 'motorcycle';
  if (path.includes('/pages/bicycle')) return 'bicycle';
  return /** @type {'motorcycle'|'bicycle'|null} */ (getCookie('gritt_vertical'));
}

/**
 * Position the sliding indicator on a toggle element.
 * Sets --toggle-pill-w and --toggle-offset so the CSS ::before pseudo slides.
 * @param {HTMLElement} toggleEl
 * @param {number} index  0 = first pill, 1 = second pill
 */
function positionToggleSlider(toggleEl, index) {
  const pills = Array.from(toggleEl.querySelectorAll('[data-vertical-option]'));
  const target = pills[index];
  if (!target) return;
  // Width of the indicator = target pill's own width
  const w = target.offsetWidth;
  // Offset = sum of all pill widths before this index (not the target's width)
  const offset = pills.slice(0, index).reduce((sum, p) => sum + p.offsetWidth, 0);
  toggleEl.style.setProperty('--toggle-pill-w', w + 'px');
  toggleEl.style.setProperty('--toggle-offset', offset + 'px');
}

/**
 * Apply aria-current and visual active class to the toggle pills.
 * Also positions the sliding indicator.
 * @param {Element} toggleEl
 * @param {string|null} vertical
 */
function updateTogglePills(toggleEl, vertical) {
  const pills = Array.from(toggleEl.querySelectorAll('[data-vertical-option]'));
  let activeIndex = 0;
  pills.forEach((pill, i) => {
    const isActive = pill.dataset.verticalOption === vertical;
    pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
    pill.setAttribute('aria-current', isActive ? 'true' : 'false');
    pill.classList.toggle('vertical-toggle__pill--active', isActive);
    pill.setAttribute('tabindex', isActive ? '0' : '-1');
    if (isActive) activeIndex = i;
  });
  positionToggleSlider(toggleEl, activeIndex);

  // Hover: pre-slide indicator toward hovered pill, restore on mouseleave
  pills.forEach((pill, i) => {
    pill.addEventListener('mouseenter', () => positionToggleSlider(toggleEl, i));
    pill.addEventListener('mouseleave', () => positionToggleSlider(toggleEl, activeIndex));
  });
}

/**
 * Navigate to the chosen vertical page and persist cookie.
 * @param {string} vertical
 */
function activateVertical(vertical) {
  if (vertical !== 'motorcycle' && vertical !== 'bicycle') return;
  setCookie('gritt_vertical', vertical, 30);
  document.body.dataset.vertical = vertical;
  window.dispatchEvent(new CustomEvent('verticalchange', { detail: { vertical } }));
  window.location.href = `/pages/${vertical}`;
}

/**
 * Reflect the active vertical on the transparent header's category pills
 * (.header__category-toggle). These are server-rendered, so on pages where the
 * server can't know the vertical (e.g. a PDP for a product in BOTH verticals),
 * this reconciles them with the browsing context. Defaults to motorcycle when
 * no vertical is resolved. The sliding indicator follows via CSS :has().
 * @param {string|null} vertical
 */
function syncCategoryToggle(vertical) {
  const toggle = document.querySelector('.header__category-toggle');
  if (!toggle) return;
  const wantBike = vertical === 'bicycle';
  toggle.querySelectorAll('.header__category-toggle__half').forEach((half) => {
    const isBike = /\/bicycle/.test(half.getAttribute('href') || '');
    const isActive = isBike === wantBike;
    half.classList.toggle('header__category-toggle__half--active', isActive);
    half.classList.toggle('header__category-toggle__half--inactive', !isActive);
    if (isActive) half.setAttribute('aria-current', 'page');
    else half.removeAttribute('aria-current');
  });
}

function initVerticalToggle() {
  const toggles = Array.from(document.querySelectorAll('[data-vertical-toggle]'));

  const vertical = resolveActiveVertical();

  if (vertical && !document.body.dataset.vertical) {
    document.body.dataset.vertical = vertical;
  }

  // Reconcile the transparent header's category pills with the active vertical,
  // and keep them in sync when the vertical changes.
  syncCategoryToggle(vertical);
  window.addEventListener('verticalchange', (e) => {
    const evt = /** @type {CustomEvent} */ (e);
    syncCategoryToggle(evt.detail?.vertical ?? null);
  });

  if (!toggles.length) return;
  const toggle = toggles[0];

  // Init all toggle instances (desktop + mobile drawer)
  toggles.forEach((t) => updateTogglePills(t, vertical));

  // Click handler — uses delegation so dynamically-injected pills also work
  toggle.addEventListener('click', (e) => {
    const pill = /** @type {HTMLElement|null} */ (
      e.target.closest('[data-vertical-option]')
    );
    if (!pill) return;
    activateVertical(pill.dataset.verticalOption ?? '');
  });

  // Keyboard: ArrowLeft / ArrowRight roving focus between pills
  toggle.addEventListener('keydown', (e) => {
    const pills = Array.from(
      toggle.querySelectorAll('[data-vertical-option]')
    );
    if (!pills.length) return;

    const focused = /** @type {HTMLElement|null} */ (
      document.activeElement?.closest('[data-vertical-option]')
    );
    if (!focused) return;

    const currentIndex = pills.indexOf(focused);

    let targetIndex = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      targetIndex = (currentIndex + 1) % pills.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      targetIndex = (currentIndex - 1 + pills.length) % pills.length;
    } else if (e.key === 'Enter' || e.key === ' ') {
      activateVertical(focused.dataset.verticalOption ?? '');
      e.preventDefault();
      return;
    }

    if (targetIndex !== -1) {
      e.preventDefault();
      pills.forEach((p, i) => {
        p.setAttribute('tabindex', i === targetIndex ? '0' : '-1');
      });
      /** @type {HTMLElement} */ (pills[targetIndex]).focus();
    }
  });
}

// ─── SHOP dropdown ────────────────────────────────────────────────────────────

/**
 * Show the correct SHOP submenu based on active vertical.
 * @param {string|null} vertical
 */
function syncShopMenus(vertical) {
  const motoMenu = document.querySelector('[data-shop-menu="motorcycle"]');
  const bikeMenu = document.querySelector('[data-shop-menu="bicycle"]');

  if (!motoMenu && !bikeMenu) return;

  const show = vertical === 'bicycle' ? bikeMenu : motoMenu;
  const hide = vertical === 'bicycle' ? motoMenu : bikeMenu;

  show?.removeAttribute('hidden');
  hide?.setAttribute('hidden', '');
}

/**
 * Close all open dropdowns.
 */
function closeAllDropdowns() {
  document.querySelectorAll('[data-has-dropdown]').forEach((parent) => {
    parent.setAttribute('aria-expanded', 'false');
    const trigger = parent.querySelector('[aria-haspopup]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  });
}

function initShopDropdown() {
  // Initial sync
  syncShopMenus(document.body.dataset.vertical ?? null);

  // Re-sync when vertical changes
  window.addEventListener('verticalchange', (e) => {
    const evt = /** @type {CustomEvent} */ (e);
    syncShopMenus(evt.detail?.vertical ?? null);
  });

  document.querySelectorAll('[data-has-dropdown]').forEach((parent) => {
    const dropdown = parent.querySelector('[data-dropdown]');
    if (!dropdown) return;
    const trigger = /** @type {HTMLElement|null} */ (parent.querySelector('[aria-haspopup]'));

    // Hover — desktop
    parent.addEventListener('mouseenter', () => {
      parent.setAttribute('aria-expanded', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    });

    parent.addEventListener('mouseleave', () => {
      parent.setAttribute('aria-expanded', 'false');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });

    // Keyboard
    parent.addEventListener('keydown', (e) => {
      const ke = /** @type {KeyboardEvent} */ (e);
      if (ke.key === 'Enter' || ke.key === ' ') {
        const isOpen = parent.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          parent.setAttribute('aria-expanded', 'false');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        } else {
          parent.setAttribute('aria-expanded', 'true');
          if (trigger) trigger.setAttribute('aria-expanded', 'true');
          // Move focus into first link in dropdown
          const firstLink = /** @type {HTMLElement|null} */ (
            dropdown.querySelector('a, button')
          );
          firstLink?.focus();
        }
        ke.preventDefault();
      } else if (ke.key === 'Escape') {
        parent.setAttribute('aria-expanded', 'false');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        /** @type {HTMLElement} */ (parent).focus();
      }
    });

    // Close when Tab leaves the dropdown area
    dropdown.addEventListener('keydown', (e) => {
      const ke = /** @type {KeyboardEvent} */ (e);
      if (ke.key === 'Escape') {
        parent.setAttribute('aria-expanded', 'false');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        /** @type {HTMLElement} */ (parent).focus();
      }

      if (ke.key === 'Tab') {
        const focusables = Array.from(
          dropdown.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])')
        );
        const last = focusables[focusables.length - 1];
        if (!ke.shiftKey && document.activeElement === last) {
          parent.setAttribute('aria-expanded', 'false');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        }
        if (ke.shiftKey && document.activeElement === focusables[0]) {
          parent.setAttribute('aria-expanded', 'false');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        }
      }
    });
  });

  // Close dropdowns on outside click
  document.addEventListener('click', (e) => {
    if (!/** @type {Element} */ (e.target).closest('[data-has-dropdown]')) {
      closeAllDropdowns();
    }
  });
}

// ─── Sticky / hide-on-scroll header ──────────────────────────────────────────

function initStickyHeader() {
  const header = /** @type {HTMLElement|null} */ (
    document.querySelector('[data-sticky-behaviour]')
  );
  if (!header) return;

  const behaviour = header.dataset.stickyBehaviour ?? 'sticky';

  if (behaviour === 'static') return;

  // ── Transparent overlay header (hero-topped pages: motorcycle/bicycle/PLP/
  // PDP). It's position:absolute over the hero and scrolls away, so CSS sticky
  // can't help. Reveal a solid pinned bar (.header--pinned) once the visitor
  // scrolls past the header — keeps nav reachable, esp. on mobile.
  if (header.classList.contains('header--transparent')) {
    // Capture the natural (unpinned) height as the reveal threshold. Re-measure
    // on resize only while unpinned so the condensed pinned height never feeds
    // back into the threshold (which would cause boundary flicker).
    let pinAt = Math.max(header.offsetHeight, 80);
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const pinned = header.classList.contains('header--pinned');
        // Hysteresis (±10px) so a resting scroll position never toggles rapidly.
        if (!pinned && y > pinAt) header.classList.add('header--pinned');
        else if (pinned && y < pinAt - 10) header.classList.remove('header--pinned');
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      if (!header.classList.contains('header--pinned')) {
        pinAt = Math.max(header.offsetHeight, 80);
      }
    }, { passive: true });
    onScroll();
    return;
  }

  // ── Non-transparent sticky headers (splash + content pages) ──
  // They stick via CSS but never condensed. Toggle .header--condensed once the
  // visitor scrolls past the top so the solid bar shrinks its logo + padding —
  // matching the compact feel of the transparent pinned bar. Runs for both the
  // default 'sticky' and 'hide_on_scroll' behaviours.
  {
    let condTicking = false;
    const condenseAt = 40;
    const onCondense = () => {
      if (condTicking) return;
      condTicking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const condensed = header.classList.contains('header--condensed');
        // Hysteresis so a resting scroll near the threshold never flickers.
        if (!condensed && y > condenseAt) header.classList.add('header--condensed');
        else if (condensed && y < condenseAt - 8) header.classList.remove('header--condensed');
        condTicking = false;
      });
    };
    window.addEventListener('scroll', onCondense, { passive: true });
    onCondense();
  }

  if (behaviour === 'hide_on_scroll') {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          if (currentScrollY > 100) {
            if (currentScrollY > lastScrollY) {
              // Scrolling down — hide
              header.classList.add('header--hidden');
            } else {
              // Scrolling up — show
              header.classList.remove('header--hidden');
            }
          } else {
            header.classList.remove('header--hidden');
          }
          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // behaviour === 'sticky' → handled by CSS position: sticky; no JS needed
}

// ─── Mobile menu ──────────────────────────────────────────────────────────────

/**
 * Collect all focusable elements within an element.
 * @param {Element} container
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
}

export function openMobileMenu() {
  const nav = document.getElementById('mobile-nav');
  const trigger = document.querySelector('[data-action="open-mobile-menu"]');
  if (!nav) return;

  // Only one right-hand panel at a time — see the same pair in search.js.
  window.GrittSearch?.close?.();
  window.GrittCart?.close?.();

  // Step 1: make the element renderable (removes display:none override)
  nav.removeAttribute('hidden');

  // Step 2: double-rAF lets the browser paint the off-screen starting state
  // before we trigger the slide-in transition via aria-hidden
  requestAnimationFrame(() => requestAnimationFrame(() => {
    nav.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mobile-menu-open');
    trigger?.setAttribute('aria-expanded', 'true');

    mobileMenuTrigger = /** @type {HTMLButtonElement|null} */ (trigger);

    // Focus the drawer itself, not its first link: on phones the browser drew the
    // red focus ring around the logo every time the menu opened (client, Oct 2026).
    // Tab still lands on the logo first.
    nav.focus({ preventScroll: true });

    nav.addEventListener('keydown', trapFocusInMobileMenu);
    document.addEventListener('keydown', closeMobileMenuOnEsc);
  }));
}

export function closeMobileMenu() {
  const nav = document.getElementById('mobile-nav');
  const trigger = document.querySelector('[data-action="open-mobile-menu"]');
  if (!nav) return;

  // Step 1: trigger the slide-out transition
  nav.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('mobile-menu-open');
  trigger?.setAttribute('aria-expanded', 'false');

  nav.removeEventListener('keydown', trapFocusInMobileMenu);
  document.removeEventListener('keydown', closeMobileMenuOnEsc);

  mobileMenuTrigger?.focus();
  mobileMenuTrigger = null;

  // Step 2: set hidden only after the slide-out animation completes
  nav.addEventListener('transitionend', (e) => {
    if (e.propertyName === 'transform') nav.setAttribute('hidden', '');
  }, { once: true });
}

/**
 * @param {KeyboardEvent} e
 */
function trapFocusInMobileMenu(e) {
  const nav = document.getElementById('mobile-nav');
  if (!nav) return;

  const focusables = getFocusableElements(nav);
  if (!focusables.length) return;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];

  if (e.key === 'Tab') {
    if (e.shiftKey) {
      if (document.activeElement === first || document.activeElement === nav) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
}

/**
 * @param {KeyboardEvent} e
 */
function closeMobileMenuOnEsc(e) {
  if (e.key === 'Escape') {
    closeMobileMenu();
  }
}

function initMobileMenu() {
  // Outside click closes the menu
  document.addEventListener('click', (e) => {
    const nav = document.getElementById('mobile-nav');
    if (!nav) return;
    if (nav.getAttribute('aria-hidden') === 'true') return;

    const target = /** @type {Element} */ (e.target);
    const clickedInsideNav = nav.contains(target);
    const clickedTrigger = target.closest('[data-action="open-mobile-menu"]');

    if (!clickedInsideNav && !clickedTrigger) {
      closeMobileMenu();
    }
  });

  // Handle for search.js, which must close this panel before opening its own.
  // A window handle, not an ESM import: theme.js is loaded from a versioned CDN
  // URL, so importing './theme.js' from another asset resolves to a different
  // module URL and would instantiate this file a second time, double-binding
  // every delegated listener in it. Mirrors window.GrittCart in cart.js.
  window.GrittNav = { openMobileMenu, closeMobileMenu };
}

// ─── Announcement bar ─────────────────────────────────────────────────────────

export function dismissAnnouncement() {
  const bar = document.querySelector('[data-announcement-bar]');
  if (!bar) return;

  if (!prefersReducedMotion) {
    /** @type {HTMLElement} */ (bar).style.transition = 'opacity 0.25s ease, max-height 0.3s ease';
    /** @type {HTMLElement} */ (bar).style.opacity = '0';
    /** @type {HTMLElement} */ (bar).style.maxHeight = '0';
    /** @type {HTMLElement} */ (bar).style.overflow = 'hidden';
    bar.addEventListener(
      'transitionend',
      () => bar.setAttribute('hidden', ''),
      { once: true }
    );
  } else {
    bar.setAttribute('hidden', '');
  }

  sessionStorage.setItem('gritt_announcement_dismissed', '1');
}

function initAnnouncementBar() {
  if (sessionStorage.getItem('gritt_announcement_dismissed') === '1') {
    const bar = document.querySelector('[data-announcement-bar]');
    bar?.setAttribute('hidden', '');
  }
}

// ─── Event delegation ─────────────────────────────────────────────────────────

function initEventDelegation() {
  document.addEventListener('click', (e) => {
    const el = /** @type {HTMLElement|null} */ (
      /** @type {Element} */ (e.target).closest('[data-action]')
    );
    if (!el) return;

    const action = el.dataset.action;

    const actions = {
      'open-mobile-menu': () => openMobileMenu(),
      'close-mobile-menu': () => closeMobileMenu(),
      'dismiss-announcement': () => dismissAnnouncement(),
    };

    actions[action ?? '']?.();
  });
}

// ─── Active nav item ──────────────────────────────────────────────────────────

/**
 * Mark the nav link that matches the current path as aria-current="page".
 */
function initActiveNavLinks() {
  // Strip trailing slash so "/pages/about/" and "/pages/about" compare equal.
  const currentPath = window.location.pathname.replace(/\/$/, '') || '/';

  // Covers desktop primary nav + the mobile drawer's top-level links.
  document.querySelectorAll('[data-main-nav] a, .mobile-nav__list a').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    // Menu links may be stored as absolute URLs (https://shop/pages/about),
    // so normalise to a pathname before comparing against the current path.
    let linkPath;
    try {
      linkPath = new URL(href, window.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch (e) {
      return;
    }
    if (linkPath === '/') return; // never mark Home active

    // Exact match, or current page nested under the link (with a "/" boundary
    // so /pages/about does not match /pages/about-us).
    if (linkPath === currentPath || currentPath.startsWith(linkPath + '/')) {
      link.setAttribute('aria-current', 'page');
      if (link.classList.contains('header__nav-link')) {
        link.classList.add('header__nav-link--active');
      }
      if (link.classList.contains('mobile-nav__link')) {
        link.classList.add('mobile-nav__link--active');
      }
    }
  });
}

// ─── Skip link ────────────────────────────────────────────────────────────────

function initSkipLink() {
  const skip = document.querySelector('[data-skip-link]');
  if (!skip) return;

  skip.addEventListener('click', (e) => {
    e.preventDefault();
    const target = document.getElementById('main-content');
    if (!target) return;
    target.setAttribute('tabindex', '-1');
    target.focus();
    target.addEventListener(
      'blur',
      () => target.removeAttribute('tabindex'),
      { once: true }
    );
  });
}

// ─── Transparent-header offset ──────────────────────────────────────────────

/**
 * Expose the transparent (overlay) header's rendered height as the CSS custom
 * property --transparent-header-h on :root. Hero sections add this to their top
 * padding so content centres in the VISIBLE area between the header's bottom
 * edge and the hero's bottom — without hardcoding a per-breakpoint guess.
 * When the header is in normal flow (not overlaying), the var stays 0 and heroes
 * centre in their own box, which is already below the header.
 */
function initHeaderOffset() {
  const root = document.documentElement;
  const header = document.querySelector('.header');
  const transparent = document.querySelector('.header--transparent');
  const apply = () => {
    // Overlay height (0 when the header is in normal flow) — heroes reserve it.
    root.style.setProperty('--transparent-header-h', transparent ? transparent.offsetHeight + 'px' : '0px');
    // Full rendered header height — the splash hero subtracts this from 100svh so
    // it fits exactly (the old 80px guess under-measured the real ~118px header,
    // pushing the hero + its SHOP links below the fold). Dedicated var: --header-height
    // is a separate contract (the compact/pinned offset for the sticky PLP filters).
    if (header) root.style.setProperty('--header-full-h', header.offsetHeight + 'px');
  };
  apply();
  // Re-measure on resize (rAF-debounced) and once webfonts settle (logo/nav
  // metrics shift when Teko/Inter swap in, changing header height).
  let raf = 0;
  window.addEventListener('resize', () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(apply);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(apply);
  }
}

// ─── Footer link-column accordions (mobile only) ─────────────────────────────
// Columns render as <details class="footer__col-accordion" open>. On desktop we
// keep them open and block the toggle (so layout is unchanged); on mobile they
// collapse like Muc-Off. Rendered open by default → no flash on desktop and a
// no-JS visitor still sees every link.

function initFooterAccordions() {
  const items = document.querySelectorAll('.footer__col-accordion');
  if (!items.length) return;

  const mq = window.matchMedia('(min-width: 768px)');

  const sync = () => {
    const desktop = mq.matches;
    items.forEach((d) => {
      // Desktop → force open; mobile → collapse by default.
      d.open = desktop;
    });
  };

  // On desktop, swallow the summary click so the column can't be collapsed.
  items.forEach((d) => {
    const summary = d.querySelector('summary');
    if (!summary) return;
    summary.addEventListener('click', (e) => {
      if (mq.matches) e.preventDefault();
    });
  });

  sync();
  mq.addEventListener('change', sync);
}

// ─── Boot ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initAnnouncementBar();
  initVerticalToggle();
  initStickyHeader();
  initHeaderOffset();
  initMobileMenu();
  initShopDropdown();
  initEventDelegation();
  initActiveNavLinks();
  initSkipLink();
  initFooterAccordions();
});
